import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Spinner, Alert } from "react-bootstrap";
import { FaArrowLeft, FaCalendarAlt, FaClock } from "react-icons/fa";

import api from "../api/axios";
import CustomNavbar from "../components/CustomNavbar";
import Footer from "../components/Footer";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

function BlogDetail() {
  const { id } = useParams();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    api
      .get(`/blogs/${id}`)
      .then(({ data }) => active && setBlog(data))
      .catch(
        (err) =>
          active &&
          setError(
            err.response?.status === 404
              ? "Article not found."
              : "Unable to load this article right now."
          )
      )
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [id]);

  return (
    <>
      <CustomNavbar />

      <style>{`
        .bdt {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;
        }
        .bdt-hero {
          position: relative; overflow: hidden;
          padding: clamp(44px, 6vw, 72px) 0;
          background:
            radial-gradient(38rem 22rem at 88% 4%, rgba(255,107,53,.28), transparent 62%),
            linear-gradient(135deg, #ff9066 0%, #f3c09f 48%, #fff8f5 100%);
          color: #0f172a;
        }
        .bdt-back {
          display: inline-flex; align-items: center; gap: 9px;
          text-decoration: none; font-weight: 700; font-size: .9rem;
          color: #0f172a; background: #ffffffcc;
          padding: 9px 18px; border-radius: 999px;
          transition: background .2s ease, transform .2s ease;
        }
        .bdt-back:hover, .bdt-back:focus-visible {
          background: #fff; color: #0f172a; transform: translateX(-3px);
        }
        .bdt-cat {
          display: inline-block; margin-top: 22px;
          background: #ffffffdd; color: var(--peach-ink);
          padding: 7px 18px; border-radius: 999px;
          font-weight: 700; font-size: .74rem; letter-spacing: .08em;
        }
        .bdt-title {
          margin-top: 16px; font-weight: 800; letter-spacing: -.02em;
          font-size: clamp(1.7rem, 4.2vw, 2.9rem); line-height: 1.18;
          max-width: 900px;
        }
        .bdt-meta {
          display: flex; flex-wrap: wrap; gap: 18px; margin-top: 16px;
          color: #6b544a; font-size: .9rem;
        }
        .bdt-meta span { display: inline-flex; align-items: center; gap: 7px; }
        .bdt-meta svg { color: var(--peach-deep); }

        /* Room at the bottom for the footer's overlapping CTA panel */
        .bdt-body { padding: clamp(40px, 5vw, 64px) 0 clamp(92px, 10vw, 130px); background: #fff; }
        .bdt-img {
          width: 100%; max-height: 440px; object-fit: cover;
          border-radius: 20px; margin-bottom: 30px;
          box-shadow: 0 20px 44px rgba(15,23,42,.18);
        }
        /* Standfirst — visually distinct from the body copy */
        .bdt-lead {
          color: #334155; font-size: 1.12rem; line-height: 1.85; font-weight: 500;
          border-left: 4px solid var(--peach); padding-left: 20px; margin-bottom: 28px;
        }
        .bdt-content {
          color: #334155; line-height: 1.95; font-size: 1.02rem;
          white-space: pre-wrap;
        }
        @media (prefers-reduced-motion: reduce) {
          .bdt-back { transition: none; }
          .bdt-back:hover { transform: none; }
        }
      `}</style>

      <div className="bdt">
        <section className="bdt-hero">
          <Container style={{ position: "relative", zIndex: 1 }}>
            {/* A real link, not a history-only button — middle-click works */}
            <Link to="/blog" className="bdt-back">
              <FaArrowLeft />
              Back to Blog
            </Link>

            {blog && (
              <>
                {blog.category && <span className="bdt-cat">{blog.category}</span>}

                <h1 className="bdt-title">{blog.title}</h1>

                <div className="bdt-meta">
                  <span>
                    <FaCalendarAlt />
                    {formatDate(blog.createdAt)}
                  </span>
                  {blog.readTime && (
                    <span>
                      <FaClock />
                      {blog.readTime}
                    </span>
                  )}
                </div>
              </>
            )}
          </Container>
        </section>

        <section className="bdt-body">
          <Container style={{ maxWidth: "820px" }}>
            {loading && (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "#ff6b35" }} />
              </div>
            )}

            {!loading && error && (
              <Alert variant="danger" className="text-center">
                {error}
              </Alert>
            )}

            {!loading && !error && blog && (
              <>
                {blog.image && (
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="bdt-img"
                    loading="lazy"
                  />
                )}

                <p className="bdt-lead">{blog.description}</p>
                <div className="bdt-content">{blog.content}</div>
              </>
            )}
          </Container>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default BlogDetail;
